import queryString from 'qs';
import { flowTypes, pageConfig, removeRefCodeFromSessionStorage } from '@/common/service/page-config';
import { logger } from '@/utils/log';
import { gdprCall } from '@/apis/signing';
import { getBasicConfig } from "@/apis";
import { isEmptyVal, noopPromise } from '@/utils';
import { billingConfig } from '@/apis/config';
import store from '@/store/index';
import { APM, WISE_SUCCESS_URL, WISE_UPGRADE_SUCCESS_URL, STRIPE_APM_OPTIONS } from '@/views/checkout/payment/helper/constant';
import { UpdateTypes } from '@/common/service/checkout-utils';
import { get } from 'lodash';
import { BILLING_CYCLE, planTypes, MRS_STATUS_TYPE_MAP, BUSINESS_TYPE_QUERY_KEY } from '@/common/constants';
import { getTrackingFlowType } from '@/aspects/trackings';
import { localNumberStore, tollFreeNumberStore } from '@/plans/ZOOM_PHONE/store';
import { computedCallingPlans, computedPbxPlans, PBX_SUB_TYPES } from '@/common/service/computers/plans/pbx';
import { MIGRATE_FLOW_TYPES } from '@/plans/ZOOM_MEETING/dialog/migration-plan';
import { getSuccessPageUrlFn, proceedPayNowUrlFn } from '@/views/succezz/utils';
import { APM_PAYMENT_METHODS } from '@/common/constants';
import { setShoppingCartPersist } from '@/views/succezz/utils';
import { afterPurchaseSuccessHandlerInModalCheckout, showiFrameContainerLoading, showModalDialogCloseButton } from '@/utils/modal-utils';
import { checkMRSChange } from '@/utils/validator';
import { successTempEventStatus } from '@/common/service/temp-flow-data';

const gdprOptCall = async (params) => {
  const unabledToReach = () => { /* nothing handler */ }
  if(Reflect.has(params, 'opt') && Reflect.get(params, 'source') === 'USER_SIGNUP') {
    try {
      await gdprCall(params)
    } catch(e) {
      unabledToReach(e);
    }
  }
}

function getReferrerProduct() {
  const url = document?.referrer;
  const referrerRegExp = {
    [planTypes.WEBINAR]: url => /\/webinar/.test(url),
  };
  return Object.keys(referrerRegExp).find(k => {
    return referrerRegExp[k](url);
  });
}

export const gotoWiseSuccessPage = ({ timeout = 2000, wiseStateValue, flowType = null }) => {
  const trackingFlowType = flowType || getTrackingFlowType();
  // Make a timeout here to make sure Google Analytics has enough time to gather the statistics ZOOM-528857
  setTimeout(() => {
    window.location.href = `${WISE_SUCCESS_URL}?state=${wiseStateValue}&trackingFlowType=${encodeURIComponent(trackingFlowType)}`;
  }, timeout);
}

export const wiseUpgradeSuccessPage = ({ timeout = 2000, actionType = 0 }) => {
  const isDowngrade = isApmDowngrade(actionType)
  if (isDowngrade) {
    upgradeSuccessPage()
    return
  }

  // after success purchase, need post message to call
  if (window?.$context?.is?.modal) {
    afterPurchaseSuccessHandlerInModalCheckout();
  }

  // Make a timeout here to make sure Google Analytics has enough time to gather the statistics ZOOM-528857
  setTimeout(() => {
    window.location.href = `${WISE_UPGRADE_SUCCESS_URL}&trackingFlowType=${encodeURIComponent(getTrackingFlowType())}`;
  }, timeout);
}

export const isApmDowngrade = (actionType = 0) => {
  const { currentEditPlan, getSelectedPlans, isProrated } = store.getters
  const oldPlan = currentEditPlan
  const newPlan = get(getSelectedPlans, '[0]', {})
  const oldQuantity= oldPlan?.cartInfo?.selected_number
  const newQuantity = newPlan?.cartInfo?.selected_number
  const annualToMonthly = oldPlan?.billing_cycle === BILLING_CYCLE.ANNUAL && newPlan?.billing_cycle === BILLING_CYCLE.MONTHLY
  if (isProrated && (!isFurikomiWithNoBalance() || isKlarnaOrP24CitFlow())) {
    return false
  } else if ([UpdateTypes.BIZ_TO_PRO, UpdateTypes.BIZ_PLUS_TO_BIZ, UpdateTypes.DOWNGRADE_TO_ZONE_BASIC_PLUS].includes(actionType)) {
    return true
  } else if (newQuantity < oldQuantity) {
    return true
  } else if (annualToMonthly) {
    return true
  } else if (checkMRSChange({ oldPlan, nextPlan: newPlan }) === MRS_STATUS_TYPE_MAP.CHANGE_FUTURE) {
    return true
  }
  return false
}

const apmAfterPurchaseSuccessHandlerInModalCheckout = () => {
  return new Promise((resolve) => {
    setTimeout(() => {
      afterPurchaseSuccessHandlerInModalCheckout();
      resolve()
    }, 3000)
  })
}

export const gotoSuccessPage = async (postParams = {}, timeout = 2000, _query = {}) => {
  const { getters } = store;
  const { apmInfo } = getters;
  const { isApm, apmPaymentMethodName } = apmInfo;
  // needed for APM Free2Paid Upgrade
  if (window?.$context?.is?.modal) {
    if (postParams?.integrationType === APM) {
      apmPaymentMethodName === APM_PAYMENT_METHODS.FURIKOMI &&
        setShoppingCartPersist({
          isAPM: true,
          apmName: apmPaymentMethodName,
        });
      await apmAfterPurchaseSuccessHandlerInModalCheckout();
    } else {
      afterPurchaseSuccessHandlerInModalCheckout();
    }
  }
  // Remove Referral Program 'refCode' from sessionStorage
  removeRefCodeFromSessionStorage()
  const { type, plan, from, pageType } = pageConfig || {};
  const { _: namespace } = queryString.parse(location.search.slice(1)) || {};
  const isTrial2paid = type === flowTypes.trial2paid
  const queryObj = {
    type,
    _: namespace,
    plan,
    from,
    ..._query,
    ...(isApm && {
      apmMethodName: apmPaymentMethodName,
    }),
    pageType,
  }
  if (getters.hasZoomDevice) {
    queryObj.hasZoomDevice = true
  }

  const query = queryString.stringify(queryObj);

  logger.info("gotoSuccessPage", query, postParams);

  if (isKlarnaOrP24CitFlow() && isTrial2paid) {
    return setTimeout(() => {
      proceedPayNowUrlFn(query, `/opc/buy/apmSuccess?${query}`, '_self')();
    }, timeout);
  }
  // Make a timeout here to make sure Google Analytics has enough time to gather the statistics ZOOM-528857
  setTimeout(() => {
    if(Reflect.has(postParams, 'GDPRParams')){
      gdprOptCall(postParams.GDPRParams);
      delete postParams.GDPRParams;
    }
    getSuccessPageUrlFn(query,`/opc/buy/zsuccess?${query}`)(postParams);
  }, timeout);
};

const successPageMigrationFlowType = {
  [MIGRATE_FLOW_TYPES.pro2biz]: flowTypes.pro2biz,
  [MIGRATE_FLOW_TYPES.biz2bundle]: flowTypes.biz2bundle,
  [MIGRATE_FLOW_TYPES.pro2prop]: flowTypes.pro2prop,
  [MIGRATE_FLOW_TYPES.prop2bundle]: flowTypes.prop2bundle,
}

export const upgradeSuccessPage = (defaultType, actionType, timeout=2000, curEditPlanType, apmModalSuccessPageNeeded = false) => {
  if (window?.$context?.is?.modal) {
    if (pageConfig?.skipSuccessPage && apmModalSuccessPageNeeded) {
      showiFrameContainerLoading();
      showModalDialogCloseButton();
    } else {
      afterPurchaseSuccessHandlerInModalCheckout();
    }
  }

  const { getters } = store;
  const { apmInfo } = getters;
  const { isApm, apmPaymentMethodName } = apmInfo;
  if(!!isApm && !!apmPaymentMethodName && apmPaymentMethodName === APM_PAYMENT_METHODS.FURIKOMI) {
    setShoppingCartPersist({
      isAPM: true,
      apmName: apmPaymentMethodName
    });
  }
  const klarnaOrP24CitFlow = isKlarnaOrP24CitFlow()
  if (getters.migratePlanState.is.upgrade && !klarnaOrP24CitFlow) {
    const flowType = successPageMigrationFlowType[getters.migratePlanState.migrateType];
    if (flowType) {
      return gotoSuccessPage({}, 2000, {
        type: flowType
      });
    }
  }
  const eventResult = successTempEventStatus.getValue() ?? {};
  const { type, plan, from, from_sub, subPlan, pageType, softWbnSub } = pageConfig || {};
  const { _: namespace } = queryString.parse(location.search.slice(1)) || {};
  const hasZoomDevice = getters.hasZoomDevice
  const queryObj = {
    type: defaultType || type,
    _: namespace,
    plan,
    from,
    from_sub,
    subPlan,
    curEditPlanType,
    pageType,
    softWbnSub,
    ...(isApm && !isApmDowngrade(actionType) && !isFurikomiWithNoBalance() &&{
      apmMethodName: apmPaymentMethodName,
      skipSuccessPage: pageConfig?.skipSuccessPage
    }),
    ...(isEmptyVal(eventResult?.businessType) ? {} : { [BUSINESS_TYPE_QUERY_KEY]: eventResult?.businessType })
  }

  if (hasZoomDevice) {
    queryObj.hasZoomDevice = true
  }
  const query = queryString.stringify(queryObj);
  // during klarna or P24 P2P flow, when there is an outstanding balance, we need to bring the user on session to complete payment
  if (klarnaOrP24CitFlow && !isApmDowngrade(actionType)) {
    return setTimeout(() => {
      proceedPayNowUrlFn(query, `/opc/buy/apmSuccess?${query}`, '_self')();
    }, timeout);
  }
  // Make a timeout here to make sure Google Analytics has enough time to gather the statistics ZOOM-528857
  setTimeout(() => {
    getSuccessPageUrlFn(query, `/opc/buy/updateSuccess?${query}`, '_self')();
  }, timeout);
};

export const buyTrialSuccessPage = (postParams = {}, defaultType, timeout = 2000) => {
  const { type, plan, from, from_sub, pageType } = pageConfig || {};
  const query = {
    type: defaultType || type,
    plan,
    from: from || from_sub,
    pageType,
  };
  return gotoSuccessPage(postParams, timeout, query);
};

export const addTrialSuccessPage = (defaultType, timeout = 2000) => {
  const { getters } = store;
  const { apmInfo } = getters;
  const { isApm, apmPaymentMethodName } = apmInfo;
  const { type, plan, from, from_sub, pageType } = pageConfig || {};
  const { _: namespace } = queryString.parse(location.search.slice(1)) || {};
  const query = queryString.stringify({
    type: defaultType || type,
    _: namespace,
    plan,
    from: from || from_sub,
    pageType,
    ...(getReferrerProduct() ? { refer: getReferrerProduct() }: {}),
    ...(isApm && {
      apmMethodName: apmPaymentMethodName,
    }),
  });

  if (window?.$context?.is?.modal) {
    afterPurchaseSuccessHandlerInModalCheckout();
  }

  // Make a timeout here to make sure Google Analytics has enough time to gather the statistics ZOOM-528857
  setTimeout(() => {
    getSuccessPageUrlFn(query,`/opc/buy/addTrialSuccess?${query}`, '_self')();
  }, timeout);
};

export const gotoTrialSuccessPage = (postParams = {}, timeout = 2000) => {
  const { type, plan, from } = pageConfig || {};
  const { _: namespace } = queryString.parse(location.search.slice(1)) || {};
  const query = queryString.stringify({ type, _: namespace, plan, from  });

  logger.info("gotoTrialSuccessPage", query, postParams);

  // Make a timeout here to make sure Google Analytics has enough time to gather the statistics ZOOM-528857
  setTimeout(() => {
    if(Reflect.has(postParams, 'GDPRParams')){
      gdprOptCall(postParams.GDPRParams);
      delete postParams.GDPRParams;
    }
    window.SB?.postForm(`/buy/trial_success?${query}`, postParams);
  }, timeout);
};

export const cachePromise = (func) => {
  let m = new Map();
  return async function (...args) {
    const params = args.map(a => a instanceof FormData ? Object.fromEntries(a) : a);
    const key = JSON.stringify(params);

    if (!m.has(key)) {
      const p = async () => {
        const res = await func(...args);
        m.delete(key);
        return res;
      }
      m.set(key, p());
    }
    return m.get(key);
  }
}

export class CacheApi {
  isIdle = true;
  result = null;
  resultPromise = null;
  api = noopPromise;
  constructor(api) {
    this.api = api;
  }
  cachedApiFn = async (...args) => {
    try {
      if(this.result && this.isIdle) {
        this.resultPromise = Promise.resolve(this.result);
      } else if(!this.resultPromise) {
        this.resultPromise = this.api(...args);
      }
      this.isIdle = false;
      this.result = await this.resultPromise;
      this.isIdle = true;
      return this.result;
    } catch (error) {
      this.result = null;
      this.isIdle = true;
      this.resultPromise = null;
      return {};
    }
  }
}

export const getBasicConfig$Cache = new CacheApi(getBasicConfig).cachedApiFn;
export const billingConfig$Cache = new CacheApi(billingConfig).cachedApiFn;

export const getPreAssignPhoneNumber = () => {
  const _preAssignPhoneNumber = {};
  const getters = store.getters;
  const localNumberState = localNumberStore.state;
  const tollFreeNumberState = tollFreeNumberStore.state;
  const selectedCallingPlan = computedCallingPlans(getters.getSelectedPlans)[0];
  const selectedTFPlan = computedPbxPlans(getters.getSelectedPlans).find(item => item.sub_type === PBX_SUB_TYPES.TOLLFREE);

  if (selectedCallingPlan && localNumberState.showNumberList) {
    _preAssignPhoneNumber.tollNumbers = localNumberState.pickedNumberList.map(item => ({
      ...item,
      planCode: selectedCallingPlan.plan_code,
    }))
  }
  if (selectedTFPlan && tollFreeNumberState.showNumberList) {
    _preAssignPhoneNumber.tollFreeNumbers = tollFreeNumberState.pickedNumberList.map(item => ({
      ...item,
      planCode: selectedTFPlan.plan_code,
    }));
  }
  return _preAssignPhoneNumber;
}

export const isKlarnaOrP24CitFlow = () => {
  const { isProrated, getTodaysTotal, apmInfo } = store.getters
  const { isApm, apmPaymentMethodName } = apmInfo;
  const isP24 = isApm && apmPaymentMethodName === APM_PAYMENT_METHODS.P24;
  const isKlarna = isApm && apmPaymentMethodName === APM_PAYMENT_METHODS.KLARNA;
  const isPix = isApm && apmPaymentMethodName === STRIPE_APM_OPTIONS.PIX;
  const hasOutstandingBalance = isProrated ? getTodaysTotal > 0 : true
  const result = hasOutstandingBalance && (isP24 || isKlarna || isPix)
  return result
}

const isFurikomiWithNoBalance = () => {
  const { isProrated, getTodaysTotal, apmInfo } = store.getters
  const { apmPaymentMethodName } = apmInfo;
  const hasOutstandingBalance = isProrated ? getTodaysTotal > 0 : true
  return !hasOutstandingBalance && apmPaymentMethodName === APM_PAYMENT_METHODS.FURIKOMI
}
